import { Button, Checkbox, Flex, Skeleton, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import arrow from "../../../assets/Icons/allocateCaseArrow.svg";
import { ReactComponent as EditIcon } from "../../../assets/Icons/Editicon.svg";
import { ReactComponent as StarIcon } from "../../../assets/Icons/starIcon.svg";
import { ReactComponent as StarMarkedIcon } from "../../../assets/Icons/starMarkedIcon.svg";
import ReusableTable from "../../../Reuseable/ReusableTable/ReusableTable";
import EditAllocationModal from "../EditAllocationModal/EditAllocationModal";
import UserAllocationDetails from "../UserAllocationDetails/UserAllocationDetails";
import "./AllCases.scss";
import {
  PhoneFilled,
  RightCircleFilled,
  UndoOutlined,
} from "@ant-design/icons";
import CheckableTag from "antd/es/tag/CheckableTag";
import ContactsModal from "../../ContactsModal/ContactsModal";
import { CasesFeedbackModal } from "../FeedbackModal/CasesFeedback";

const AllCases = ({
  data = [],
  selectedTab,
  onSelectionChange,
  unAllocatedCasesData = [],
  allocatedCasesData = [],
  onRowClick = () => {},
  pointer = false,
  unAllocatedCount,
  allocatedCount,
  onPaginationChange,
  setRefresh,
  setIsAll,
  isAll,
}) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [caseType, setCaseType] = useState(null);
  const [bookmarkedLoans, setBookmarkedLoans] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [openContactsModal, setOpenContactsModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);

  const { unAllocated_case_loading, allocated_case_loading } = useSelector(
    (state) => state.allocation,
  );
  const { telecaller_cases, telecaller_cases_loading } = useSelector(
    (state) => state.telecallerCases,
  );
  const { user } = useSelector((state) => state.auth);

  const loading =
    selectedTab === "Allocated"
      ? allocated_case_loading || telecaller_cases_loading
      : unAllocated_case_loading;

  const navigate = useNavigate();

  const handleEdit = (record) => {
    setSelectedRow(record);
    setCaseType(selectedTab === "Allocated" ? "allocated" : "unallocated");
    setEditModalOpen(true);
  };

  const handleViewDetails = (record, type) => {
    setSelectedRowData(record);
    setCaseType(type);
    setShowDetails(true);
  };

  const toggleBookmark = (loanId) => {
    setBookmarkedLoans((prev) => ({
      ...prev,
      [loanId]: !prev[loanId],
    }));
  };

  useEffect(() => {
    const casesData =
      selectedTab === "Unallocated" ? unAllocatedCasesData : allocatedCasesData;
    if (isAll) {
      const allKeys = casesData.map((row) => row.id);
      setSelectedLoanKeys(allKeys);
      setSelectedIds(allKeys);
      onSelectionChange?.(allKeys);
    } else {
      setSelectedLoanKeys([]);
      setSelectedIds([]);
      onSelectionChange?.([]);
    }
  }, [isAll, unAllocatedCasesData, allocatedCasesData, selectedTab]);

  useEffect(() => {
    setSelectedLoanKeys([]);
    setSelectedIds([]);
    onSelectionChange?.([]);
    setIsAll(false);
  }, [selectedTab]);

  const actionColumn = {
    title: "Action",
    key: "action",
    render: (_, record) =>
      user?.is_tc ? (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            setOpenContactsModal(true);
            setSelectedCase(record);
          }}
        >
          Feedback
        </Button>
      ) : (
        <Flex gap={8} align="center">
          <Button
            className="editIcon"
            icon={<EditIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
            disabled={loading}
          />
          <Button
            className="detailsBtn"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(
                record,
                selectedTab === "Allocated" ? "allocated" : "unallocated",
              );
            }}
            disabled={loading}
          >
            Details
            <img src={arrow} alt="" />
          </Button>
        </Flex>
      ),
  };

  const bookmarkColumn = {
    title: "",
    key: "bookmark",
    onCell: () => ({
      onClick: (e) => e.stopPropagation(),
    }),
    render: (_, record) => {
      const isBookmarked = bookmarkedLoans[record.loan] || false;
      return (
        <span
          onClick={(e) => {
            e.stopPropagation();
            toggleBookmark(record.loan);
          }}
          style={{
            cursor: "pointer",
            fontSize: "18px",
            color: isBookmarked ? "#faad14" : "#999",
          }}
        >
          {isBookmarked ? <StarMarkedIcon /> : <StarIcon />}
        </span>
      );
    },
  };

  const baseColumns = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    {
      title: "Loan No.",
      dataIndex: "loan_no",
      key: "loan_no",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    {
      title: "POS",
      dataIndex: "odpos",
      key: "odpos",
      render: (text) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          Number(text).toFixed(2) || "-"
        ),
    },
    {
      title: "Aging Bucket",
      dataIndex: "bucket",
      key: "bucket",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    {
      title: "Amount Overdue",
      dataIndex: "amount_overdue",
      key: "amount_overdue",
      sorter: true,
      render: (text) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : (
          Number(text).toFixed(2) || "-"
        ),
    },
    {
      title: "Bank",
      dataIndex: "bank",
      key: "bank",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    {
      title: "Branch",
      dataIndex: "branch_name",
      key: "branch_name",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    {
      title: "Languages",
      dataIndex: "languages",
      key: "languages",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
      render: (text) =>
        loading ? (
          <Skeleton.Input size="small" active />
        ) : Array.isArray(text) ? (
          text.length > 0 ? (
            text.join(", ")
          ) : (
            "-"
          )
        ) : (
          text || "-"
        ),
    },
  ];

  const allocatedColumns = [
    ...baseColumns,
    {
      title: "Assigned Telecaller",
      dataIndex: "assigned_telecaller",
      key: "assigned_telecaller",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    {
      title: "Assigned FieldAgent",
      dataIndex: "assigned_fieldagent",
      key: "assigned_fieldagent",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    {
      title: "Team Lead",
      dataIndex: "allocated_teamlead",
      key: "allocated_teamlead",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    {
      title: "Supervisor",
      dataIndex: "allocated_supervisor",
      key: "allocated_supervisor",
      render: (text) =>
        loading ? <Skeleton.Input size="small" active /> : text || "-",
    },
    actionColumn,
  ];

  const unallocatedColumns = [bookmarkColumn, ...baseColumns, actionColumn];

  const [selectedLoanKeys, setSelectedLoanKeys] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // const handleSelectAll = () => {
  //   setIsAll(!isAll)
  // }

  // Custom rowSelection to render Test button and select all checkbox in header
  const handleTestClick = () => {
    window.alert("Test button clicked!");
  };

  const casesData =
    selectedTab === "Unallocated" ? unAllocatedCasesData : allocatedCasesData;

  const customRowSelection = !loading
    ? {
        columnTitle: (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Checkbox
                checked={
                  selectedLoanKeys.length === casesData.length &&
                  casesData.length > 0
                }
                indeterminate={
                  selectedLoanKeys.length > 0 &&
                  selectedLoanKeys.length < casesData.length
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    const allKeys = casesData.map((row) => row.id);
                    setSelectedLoanKeys(allKeys);
                    setSelectedIds(allKeys);
                    onSelectionChange?.(allKeys);
                    setIsAll(true);
                  } else {
                    setSelectedLoanKeys([]);
                    setSelectedIds([]);
                    onSelectionChange?.([]);
                    setIsAll(false);
                  }
                }}
                style={{ marginTop: 2 }}
              />
            </div>
          </div>
        ),
        selectedRowKeys: selectedLoanKeys,
        onChange: (keys, selectedRows) => {
          setSelectedLoanKeys(keys);
          setSelectedIds(selectedRows.map((row) => row.id));
          onSelectionChange?.(selectedRows.map((row) => row.id));
        },
      }
    : null;

  return (
    <div className="allCases">
      <ReusableTable
        columns={
          selectedTab === "Unallocated" ? unallocatedColumns : allocatedColumns
        }
        dataSource={
          loading
            ? Array(5)
                .fill(0)
                .map((_, i) => ({ id: `skeleton-${i}` }))
            : selectedTab === "Unallocated"
              ? unAllocatedCasesData
              : allocatedCasesData
        }
        totalItems={
          selectedTab === "Unallocated" ? unAllocatedCount : allocatedCount
        }
        rowKey="id"
        rowSelection={customRowSelection}
        onRowClick={onRowClick}
        pointer={pointer}
        CurrentPageNo={currentPage}
        pageSize={pageSize}
        triggerChange={(page, size) => {
          onPaginationChange(page, size);
          setCurrentPage(page);
          setPageSize(size);
        }}
      />
      <EditAllocationModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        record={selectedRow}
        type={caseType}
        setRefresh={setRefresh}
      />
      {/* <ContactsModal
        open={openContactsModal}
        setOpen={setOpenContactsModal}
        data={selectedCase}
      /> */}
      <CasesFeedbackModal
        open={openContactsModal}
        setOpen={setOpenContactsModal}
        data={selectedCase}
        setRefresh={setRefresh}
      />
      {showDetails && selectedRowData && (
        <UserAllocationDetails
          open={showDetails}
          onClose={() => setShowDetails(false)}
          record={selectedRowData}
          type={caseType}
        />
      )}
    </div>
  );
};

export default AllCases;
